import React, { useContext, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Button, { ButtonColors } from '../../common/Button';
import { ToastContext, ToastTypes } from '../../common/contexts/toast.context';
import SanityImageUploader from '../../common/form/SanityImageUploader';
import HeroImageEditor from '../../common/form/HeroImageEditor';
import { Input } from '../../common/form/Input';
import { InputMasked } from '../../common/form/InputMasked';
import { Textarea } from '../../common/form/Textarea';
import WYSIWYG from '../../common/form/WYSIWYG';
import Header from '../../common/Header';
import LoadingPage from '../../common/LoadingPage';
import { sanityReadClient, sanityWriteClient } from '../../sanity/client';

interface PostForm {
  title: string;
  slugCurrent: string;
  author: string;
  heroImage: string;
  heroImageAlt: string;
  heroImageTransform: string;
  summary: string;
  publishDate: string;
  body: string;
}

const slugify = (text: string) =>
  text
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/(^-|-$)/g, '');

const BlogDetail = () => {
  const { createToast } = useContext(ToastContext);
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const isNew = id === 'new';

  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [sanityId, setSanityId] = useState<string | null>(null);

  const [form, setForm] = useState<PostForm>({
    title: '',
    slugCurrent: '',
    author: '',
    heroImage: '',
    heroImageAlt: '',
    heroImageTransform: '50 50 1.00',
    summary: '',
    publishDate: new Date().toISOString().slice(0, 10),
    body: '',
  });

  const patch = (field: keyof PostForm) => (val: any) => {
    setForm((prev) => {
      const updated = { ...prev, [field]: val };
      // Auto-generate slug from title for new posts
      if (field === 'title' && isNew) {
        updated.slugCurrent = slugify(val);
      }
      return updated;
    });
  };

  useEffect(() => {
    if (isNew) return;
    sanityReadClient
      .fetch(
        `*[_type == "post" && _id == $id][0]{
          _id, title, slug, author, heroImage, heroImageAlt, heroImageTransform, summary, publishDate, body
        }`,
        { id }
      )
      .then((data: any) => {
        if (data) {
          setSanityId(data._id);
          setForm({
            title: data.title ?? '',
            slugCurrent: data.slug?.current ?? '',
            author: data.author ?? '',
            heroImage: data.heroImage ?? '',
            heroImageAlt: data.heroImageAlt ?? '',
            heroImageTransform: data.heroImageTransform ?? '50 50 1.00',
            summary: data.summary ?? '',
            publishDate: data.publishDate ?? '',
            body: data.body ?? '',
          });
        }
      })
      .catch(() => {
        createToast({
          title: 'Error',
          description: <>Failed to load post.</>,
          type: ToastTypes.Fail,
          duration: 5000,
        });
      })
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const save = async () => {
    if (!form.title) {
      createToast({
        title: 'Validation error',
        description: <>Title is required.</>,
        type: ToastTypes.Fail,
        duration: 4000,
      });
      return;
    }
    if (!process.env.REACT_APP_SANITY_TOKEN) {
      createToast({
        title: 'Configuration error',
        description: <>REACT_APP_SANITY_TOKEN is not set. Add your Sanity write token to the .env file.</>,
        type: ToastTypes.Fail,
        duration: 8000,
      });
      return;
    }

    setSaving(true);
    const doc = {
      _type: 'post',
      title: form.title,
      slug: { _type: 'slug', current: form.slugCurrent || slugify(form.title) },
      author: form.author,
      heroImage: form.heroImage || undefined,
      heroImageAlt: form.heroImageAlt || undefined,
      heroImageTransform: form.heroImageTransform || undefined,
      summary: form.summary || undefined,
      publishDate: form.publishDate || undefined,
      body: form.body || undefined,
    };

    try {
      let saved: any;
      if (isNew) {
        saved = await sanityWriteClient.create(doc);
        navigate('/blog');
      } else if (sanityId) {
        saved = await sanityWriteClient.patch(sanityId).set(doc).commit();
        setSanityId(saved._id);
      }
      createToast({
        title: 'Saved!',
        description: <>Blog post saved successfully.</>,
        type: ToastTypes.Success,
        duration: 4000,
      });
    } catch (err: any) {
      createToast({
        title: 'Error',
        description: <>{err?.message || 'Failed to save post.'}</>,
        type: ToastTypes.Fail,
        duration: 6000,
      });
    } finally {
      setSaving(false);
    }
  };

  const deletePost = async () => {
    if (!sanityId) return;
    if (!window.confirm('Delete this post? This cannot be undone.')) return;
    setDeleting(true);
    try {
      await sanityWriteClient.delete(sanityId);
      createToast({
        title: 'Deleted',
        description: <>Blog post deleted successfully.</>,
        type: ToastTypes.Success,
        duration: 4000,
      });
      navigate('/blog');
    } catch (err: any) {
      createToast({
        title: 'Error',
        description: <>{err?.message || 'Failed to delete post.'}</>,
        type: ToastTypes.Fail,
        duration: 6000,
      });
    } finally {
      setDeleting(false);
    }
  };

  if (loading) return <LoadingPage />;

  return (
    <>
      <Header title={isNew ? 'New article' : form.title || 'Edit article'} />
      <div className="grid grid-cols-1 sm:grid-cols-6 gap-4 p-4">
        <Input
          name="title"
          label="Title *"
          value={form.title}
          onChange={patch('title')}
          className="col-span-6"
        />
        <Input
          name="slugCurrent"
          label="Slug (URL path)"
          value={form.slugCurrent}
          onChange={patch('slugCurrent')}
          className="col-span-6"
        />
        <Input
          name="author"
          label="Author"
          value={form.author}
          onChange={patch('author')}
          className="col-span-3"
        />
        <InputMasked
          name="publishDate"
          value={form.publishDate}
          onChange={patch('publishDate')}
          options={{
            date: true,
            delimiter: '-',
            datePattern: ['Y', 'm', 'd'],
          }}
          label="Publish date (YYYY-MM-DD)"
          placeholder="YYYY-MM-DD"
        />
        <SanityImageUploader
          name="heroImage"
          label="Hero Image"
          value={form.heroImage}
          onChange={patch('heroImage')}
          className="col-span-6"
        />
        <Input
          name="heroImageAlt"
          label="Hero Image Alt Text"
          value={form.heroImageAlt}
          onChange={patch('heroImageAlt')}
          className="col-span-6"
        />
        {form.heroImage && (
          <HeroImageEditor
            imageUrl={form.heroImage}
            value={form.heroImageTransform}
            onChange={patch('heroImageTransform')}
          />
        )}
        <Textarea
          name="summary"
          label="Summary (max 160 characters)"
          value={form.summary}
          onChange={patch('summary')}
          className="col-span-6"
          maxLen={160}
        />
        <WYSIWYG
          name="body"
          label="Article Body *"
          value={form.body}
          onChange={patch('body')}
          className="col-span-6"
          patchValue={(val: any) => {
            // WYSIWYG uses 'article' key internally; map it to 'body' for Sanity
            if ('article' in val) {
              setForm((prev) => ({ ...prev, body: val.article }));
            } else {
              setForm((prev) => ({ ...prev, ...val }));
            }
          }}
        />
        <div className="col-span-6 flex gap-3">
          <Button
            text="Save"
            onClick={save}
            loading={saving}
            disabled={saving || deleting}
          />
          {!isNew && sanityId && (
            <Button
              text="Delete"
              onClick={deletePost}
              loading={deleting}
              disabled={saving || deleting}
              color={ButtonColors.red}
            />
          )}
        </div>
      </div>
    </>
  );
};

export default BlogDetail;
